import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import _ from 'lodash'
import moment from 'moment'
import { numberFormat, toBuddhistYear } from 'utils/app.utils'
import { forceTextOnDateCells } from 'utils/excel.utils'

interface ExportToExcelProps {
    data: any[]
    branches: any[]
    branchId: number
    filter: any
    filterDateStart: string
    filterDateEnd: string
    t: any
}

export default function ExportToExcel({
    data,
    branches,
    branchId,
    filter,
    filterDateStart,
    filterDateEnd,
    t
}: ExportToExcelProps) {
    const workbook = new ExcelJS.Workbook()
    const worksheet = workbook.addWorksheet('Lab Report')

    // Set title
    worksheet.mergeCells('A1:M1')
    const titleCell = worksheet.getCell('A1')
    titleCell.value = t('REPORT.REPORT_NAME.LAB')
    titleCell.font = { bold: true, size: 16 }
    titleCell.alignment = { horizontal: 'center' }

    // Set filters horizontally
    const filterRow = worksheet.addRow([])
    filterRow.height = 20

    const filterLabels = [
        t('REPORT.FILTER.BRANCH'),
        t('REPORT.FILTER.DATE_START'),
        t('REPORT.FILTER.DATE_END'),
        t('USER_DOCTOR'),
        t('REPORT.FILTER.LAB_GROUP'),
        t('REPORT.FILTER.LAB_COMPANY')
    ]

    let colIndex = 0
    filterLabels.forEach((label, index) => {
        const labelCell = worksheet.getCell(3, colIndex + 1)
        labelCell.value = `${label}:`
        labelCell.font = { bold: true, size: 10 }
        colIndex++

        const valueCell = worksheet.getCell(3, colIndex + 1)
        valueCell.value = filter[index] || '-'
        valueCell.font = { size: 10 }
        colIndex++
    })

    // Add empty row
    worksheet.addRow([])

    // Define head cells based on LabReportList.tsx
    const headCells = [
        { label: t('REPORT.TABLE.LAB.#NO'), width: 8 },
        { label: t('REPORT.TABLE.LAB.DENTIST'), width: 20 },
        { label: t('REPORT.TABLE.LAB.PATIENI_CODE'), width: 15 },
        { label: t('REPORT.TABLE.LAB.SN_CODE'), width: 15 },
        { label: t('REPORT.TABLE.LAB.PATIENT_NAME'), width: 25 },
        { label: t('REPORT.TABLE.LAB.LAB_COMPANY'), width: 20 },
        { label: t('REPORT.TABLE.LAB.LAB_GROUP'), width: 20 },
        { label: t('REPORT.TABLE.LAB.LAB_NAME'), width: 25 },
        { label: t('REPORT.TABLE.LAB.REMARK'), width: 20 },
        { label: t('REPORT.TABLE.LAB.LAB_TX'), width: 15 },
        { label: t('REPORT.TABLE.LAB.LAB_FEE'), width: 15 },
        { label: t('REPORT.TABLE.LAB.DF_PERCENT'), width: 15 },
        { label: t('REPORT.TABLE.LAB.DF_LAB_FEE'), width: 15 }
    ]

    // Add header row
    const headerRow = worksheet.addRow(headCells.map(cell => cell.label))
    headerRow.font = { bold: true, size: 11 }
    headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' }
    }

    // Set column widths
    headCells.forEach((cell, index) => {
        worksheet.getColumn(index + 1).width = cell.width
    })

    // Add data rows grouped by date
    let rowNumber = 1
    const groupedData = _.groupBy(data, 'dateOrder')

    Object.keys(groupedData).forEach((dateKey) => {
        const dateData = groupedData[dateKey]

        // Add date header row
        const dateHeaderRow = worksheet.addRow([])
        dateHeaderRow.height = 25
        const dateCell = worksheet.getCell(dateHeaderRow.number, 1)
        dateCell.value = `${t('MOMENT_AT')} ${toBuddhistYear(moment(dateKey), 'DD/MM/YYYY')}`
        dateCell.font = { bold: true, size: 11 }
        worksheet.mergeCells(dateHeaderRow.number, 1, dateHeaderRow.number, headCells.length)

        // Add data rows for this date
        dateData.forEach((item, index) => {
            const {
                doctorName,
                branchCnNumber,
                snCode,
                patientName,
                labCompanyName,
                labGroupName,
                labName,
                labRemark,
                operativeName,
                labOrderPrice,
                labDoctorPercent,
                labDoctorTotal
            } = item

            const dataRow = worksheet.addRow([
                rowNumber,
                doctorName || '-',
                branchCnNumber || '-',
                snCode || '-',
                patientName || '-',
                labCompanyName || '-',
                labGroupName || '-',
                labName || '-',
                labRemark || '-',
                operativeName || '-',
                numberFormat(labOrderPrice || 0),
                `${Number(labDoctorPercent || 0)}%`,
                numberFormat(labDoctorTotal || 0)
            ])

            dataRow.font = { size: 10 }
            rowNumber++
        })

        // Add summary row for this date
        const summaryRow = worksheet.addRow([])
        summaryRow.height = 20
        const summaryLabelCell = worksheet.getCell(summaryRow.number, 1)
        summaryLabelCell.value = t('REPORT.TITLE.SUM')
        summaryLabelCell.font = { bold: true, size: 10 }
        worksheet.mergeCells(summaryRow.number, 1, summaryRow.number, 9)

        const summaryLabFeeCell = worksheet.getCell(summaryRow.number, 11)
        summaryLabFeeCell.value = numberFormat(_.sumBy(dateData, 'labOrderPrice'))
        summaryLabFeeCell.font = { bold: true, size: 10 }

        const summaryDfCell = worksheet.getCell(summaryRow.number, 13)
        summaryDfCell.value = numberFormat(_.sumBy(dateData, 'labDoctorTotal'))
        summaryDfCell.font = { bold: true, size: 10 }

        // Add empty row after each date group
        worksheet.addRow([])
    })

    // Add total summary at the end
    const totalRow = worksheet.addRow([])
    totalRow.height = 25
    const totalLabelCell = worksheet.getCell(totalRow.number, 1)
    totalLabelCell.value = `${t('REPORT.TITLE.TOTAL')} ${t('REPORT.REPORT_NAME.LAB')}`
    totalLabelCell.font = { bold: true, size: 12 }
    worksheet.mergeCells(totalRow.number, 1, totalRow.number, 9)

    const totalLabFeeCell = worksheet.getCell(totalRow.number, 11)
    totalLabFeeCell.value = numberFormat(_.sumBy(data, 'labOrderPrice'))
    totalLabFeeCell.font = { bold: true, size: 12 }

    const totalDfCell = worksheet.getCell(totalRow.number, 13)
    totalDfCell.value = numberFormat(_.sumBy(data, 'labDoctorTotal'))
    totalDfCell.font = { bold: true, size: 12 }

    // Generate and download file
    forceTextOnDateCells(workbook)
    workbook.xlsx.writeBuffer().then((buffer) => {
        const fileName = `${t('REPORT.REPORT_NAME.LAB')}_${filterDateStart}_${filterDateEnd}.xlsx`
        saveAs(new Blob([buffer]), fileName)
    })
} 